import { atom } from "recoil"

import { Plugin, PluginApprovalStatus } from "./plugins"
import persistAtom from "./recoilPersist"

/**
 * Interface for plugin search cache entry
 */
export interface PluginSearchCacheEntry {
  plugins: Plugin[]
  searchTerm: string
  categoryFilter: string
  timestamp: number
}

/**
 * Interface for plugin search state
 */
export interface PluginSearchState {
  // Current search query
  searchQuery: string

  // Currently displayed plugins (after filtering)
  displayedPlugins: Plugin[]

  // All cached plugins from API calls
  cachedPlugins: Plugin[]

  // Current category filter
  activeCategory: string

  // Loading states
  isLoading: boolean
  isSearching: boolean

  // Debounce state
  pendingSearchQuery: string

  // Cache metadata
  lastApiCall: number
  cacheValid: boolean
}

export const transformPlugins = (plugins: any[]): any[] =>
  plugins.map((plugin) => ({
    ...plugin,
    manifest: {
      ...plugin.manifest,
      approvalStatus: plugin.manifest?.approvalStatus as
        | PluginApprovalStatus
        | undefined,
    },
  }))

// Default state
const defaultPluginSearchState: PluginSearchState = {
  searchQuery: "",
  displayedPlugins: [],
  cachedPlugins: [],
  activeCategory: "all",
  isLoading: false,
  isSearching: false,
  pendingSearchQuery: "",
  lastApiCall: 0,
  cacheValid: false,
}

/**
 * Plugin search state atom
 * Manages the state for plugin search including caching and filtering
 */
export const pluginSearchState = atom<PluginSearchState>({
  key: "pluginSearchState",
  default: defaultPluginSearchState,
})

/**
 * Plugin search cache atom
 * Stores cached search results to avoid redundant API calls
 */
export const pluginSearchCacheState = atom<PluginSearchCacheEntry[]>({
  key: "pluginSearchCacheState",
  default: [],
  effects_UNSTABLE: [persistAtom],
})
